import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Button, Alert, CircularProgress, Paper, TextField, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
} from '@mui/material';
import LoginLayout from '../../components/LoginLayout/LoginLayout';
import {
  projectGroupSelectionService, SelectionStatus, GroupStatus, VerifyTicketResult,
} from '../../services/projectGroupSelectionService';

// Page publique — pas de garde d'authentification applicative : les étudiant·es s'identifient
// via CAS à chaque visite (voir /inscription). Contrairement à cette dernière (formulaire
// one-shot), cette page interroge régulièrement le serveur pendant la fenêtre de 10 minutes de
// verrouillage : le jeton est donc gardé en sessionStorage pour survivre à un rafraîchissement.
const TOKEN_KEY = 'projectGroupSelectionToken';
const POLL_INTERVAL_MS = 5000;

const STATE_LABELS: Record<GroupStatus['state'], string> = {
  available: 'Disponible',
  locked_other: 'En cours de formation',
  locked_by_you_solo: 'Votre choix — en attente d\'un binôme',
  awaiting_your_validation: 'Candidat en attente de votre validation',
  pending_validation: 'En attente de validation',
  complete: 'Complet',
};

function formatCountdown(lockExpiresAt: string | null): string | null {
  if (!lockExpiresAt) return null;
  const remainingMs = new Date(lockExpiresAt).getTime() - Date.now();
  if (remainingMs <= 0) return null;
  const totalSeconds = Math.floor(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

const ProjectGroupSelectionPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [identity, setIdentity] = useState<VerifyTicketResult | null>(null);
  const [status, setStatus] = useState<SelectionStatus | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  // Force un re-rendu régulier pour rafraîchir les comptes à rebours entre deux polls.
  const [, setTick] = useState(0);
  const [devLogin, setDevLogin] = useState('');

  const tokenRef = useRef<string | null>(sessionStorage.getItem(TOKEN_KEY));

  const refreshStatus = useCallback(async () => {
    if (!tokenRef.current) return;
    try {
      setStatus(await projectGroupSelectionService.getStatus(tokenRef.current));
      setError(null);
    } catch (e: any) {
      setError(e.message || "Erreur lors de la récupération de l'état des groupes");
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ticket = params.get('ticket');
    if (ticket) {
      // Le ticket CAS est à usage unique : le retirer de l'URL tout de suite pour qu'un
      // rechargement de page ne le renvoie pas une seconde fois ("Ticket CAS invalide").
      window.history.replaceState(null, '', window.location.pathname);
      setLoading(true);
      projectGroupSelectionService.verifyTicket(ticket)
        .then(async (result) => {
          setIdentity(result);
          sessionStorage.setItem(TOKEN_KEY, result.access_token);
          tokenRef.current = result.access_token;
          await refreshStatus();
        })
        .catch((e: any) => setError(e.message || 'Erreur lors de la vérification du ticket CAS'))
        .finally(() => setLoading(false));
      return;
    }

    if (tokenRef.current) {
      refreshStatus();
    }
  }, [refreshStatus]);

  useEffect(() => {
    if (!tokenRef.current || status?.my_state === 'complete') return;
    const interval = setInterval(() => {
      refreshStatus();
      setTick((t) => t + 1);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [refreshStatus, status?.my_state]);

  const handleCasLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = await projectGroupSelectionService.getCasLoginUrl();
      window.location.href = url;
    } catch (e: any) {
      setError(e.message || "Erreur lors de la récupération de l'URL CAS");
      setLoading(false);
    }
  };

  // Contourne le CAS (inaccessible en local) — le backend refuse cet appel hors développement.
  const handleDevLogin = async () => {
    if (!devLogin.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const result = await projectGroupSelectionService.devLogin(devLogin.trim());
      setIdentity(result);
      sessionStorage.setItem(TOKEN_KEY, result.access_token);
      tokenRef.current = result.access_token;
      await refreshStatus();
    } catch (e: any) {
      setError(e.message || 'Erreur lors de la connexion dev');
    } finally {
      setLoading(false);
    }
  };

  const runAction = async (action: (token: string) => Promise<SelectionStatus>) => {
    if (!tokenRef.current) return;
    setActionLoading(true);
    setError(null);
    try {
      setStatus(await action(tokenRef.current));
    } catch (e: any) {
      setError(e.message || "Erreur lors de l'action");
    } finally {
      setActionLoading(false);
    }
  };

  const isLoggedIn = !!tokenRef.current;

  return (
    <LoginLayout>
      <Paper sx={{ p: 4, width: '100%', maxWidth: isLoggedIn && status ? 700 : 480 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
          Formation des groupes projet
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && !isLoggedIn && (
          <>
            <Typography sx={{ mb: 3 }}>
              Connectez-vous avec vos identifiants universitaires (SSO CAS) pour choisir votre
              groupe TP WEB.
            </Typography>
            <Button variant="contained" fullWidth onClick={handleCasLogin}>
              Se connecter via CAS
            </Button>

            {import.meta.env.DEV && (
              <>
                <Divider sx={{ my: 3 }}>dev only</Divider>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Le vrai CAS n'est pas joignable en local : connexion directe par login (compte
                  déjà existant en base), refusée par le serveur hors développement.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    label="Login"
                    fullWidth
                    value={devLogin}
                    onChange={(e) => setDevLogin(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleDevLogin()}
                  />
                  <Button variant="outlined" disabled={!devLogin.trim()} onClick={handleDevLogin}>
                    Connexion dev
                  </Button>
                </Box>
              </>
            )}
          </>
        )}

        {!loading && isLoggedIn && (
          <>
            {identity && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Identité confirmée : {identity.firstname} {identity.lastname} ({identity.login}) —
                groupe TD {status?.td_group || identity.td_group}
              </Alert>
            )}

            {!status && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            )}

            {status && status.my_state === 'complete' && (
              <Alert severity="success">
                Votre groupe est complet ! Vous pouvez fermer cette page.
              </Alert>
            )}

            {status && status.my_state !== 'complete' && (
              <>
                {status.can_be_solo && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Vous êtes autorisé·e à former un monôme : votre groupe sera complet dès votre
                    premier choix.
                  </Alert>
                )}

                {status.my_state === 'locked_by_you_solo' && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Choix verrouillé, en attente d'un·e binôme
                    {(() => {
                      const group = status.groups.find((g) => g.state === 'locked_by_you_solo');
                      const countdown = group ? formatCountdown(group.lock_expires_at) : null;
                      return countdown ? ` (${countdown})` : '';
                    })()}
                    . Vous pouvez annuler tant que personne ne vous a rejoint·e.
                    <Box sx={{ mt: 1 }}>
                      <Button size="small" variant="outlined" disabled={actionLoading} onClick={() => runAction((t) => projectGroupSelectionService.cancelChoice(t))}>
                        Annuler mon choix
                      </Button>
                    </Box>
                  </Alert>
                )}

                {status.my_state === 'awaiting_your_validation' && (() => {
                  const group = status.groups.find((g) => g.state === 'awaiting_your_validation');
                  const countdown = group ? formatCountdown(group.lock_expires_at) : null;
                  return (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {group?.candidate?.firstname} {group?.candidate?.lastname} souhaite former un
                      binôme avec vous{countdown ? ` (${countdown} pour valider)` : ''}.
                      <Box sx={{ mt: 1 }}>
                        <Button size="small" variant="contained" disabled={actionLoading} onClick={() => runAction((t) => projectGroupSelectionService.validateCandidate(t))}>
                          Valider
                        </Button>
                      </Box>
                    </Alert>
                  );
                })()}

                {status.my_state === 'pending_validation' && (() => {
                  const group = status.groups.find((g) => g.state === 'pending_validation');
                  const countdown = group ? formatCountdown(group.lock_expires_at) : null;
                  return (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      En attente de validation par le/la titulaire du groupe
                      {countdown ? ` (${countdown})` : ''}.
                    </Alert>
                  );
                })()}

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Groupes TP WEB disponibles (groupe TD {status.td_group})
                </Typography>
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Groupe</TableCell>
                        <TableCell>État</TableCell>
                        <TableCell>Détail</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {status.groups.map((group) => {
                        const countdown = formatCountdown(group.lock_expires_at);
                        // 'locked_other' reste actionnable : c'est justement l'état où un·e second·e
                        // étudiant·e peut encore candidater pour former le binôme (choose() l'autorise
                        // côté backend tant qu'aucun·e candidat·e n'est déjà en attente).
                        const selectable = (group.state === 'available' || group.state === 'locked_other') && status.my_state === 'no_selection';
                        return (
                          <TableRow key={group.id} sx={{ opacity: group.state === 'complete' ? 0.6 : 1 }}>
                            <TableCell>{group.generic_name || group.id_group}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={STATE_LABELS[group.state]}
                                color={group.state === 'complete' ? 'default' : group.state === 'available' ? 'success' : 'warning'}
                              />
                            </TableCell>
                            <TableCell>
                              {group.state === 'complete' && group.members.map((m) => `${m.firstname} ${m.lastname}`).join(', ')}
                              {group.state === 'locked_other' && countdown && `libéré dans ${countdown} si personne ne le rejoint`}
                            </TableCell>
                            <TableCell align="right">
                              {selectable && (
                                <Button size="small" variant="outlined" disabled={actionLoading} onClick={() => runAction((t) => projectGroupSelectionService.chooseGroup(group.id, t))}>
                                  {group.state === 'available' ? 'Choisir' : 'Rejoindre'}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </Paper>
    </LoginLayout>
  );
};

export default ProjectGroupSelectionPage;
